import { sanitizeNullStrings } from "@/utils/sanitize";
import { useEffect, useState } from "react";
import {
	Box,
	Button,
	CircularProgress,
	Divider,
	Grid2,
	IconButton,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import type { ProductChange } from "../models/ProductChange";
import { ProductSizeSelector } from "./ProductSizeSelector";

interface ProductChangeDetailViewerProps {
	change: ProductChange | null;
	isSaving: boolean;
	isRecalculating: boolean;
	onSave: (change: ProductChange) => void;
	onClose: () => void;
	onRecalculate: (id: number) => void;
}

const sectionHeaderSx = {
	color: "primary.main",
	fontWeight: 600,
	fontSize: "0.85rem",
	mt: 1.5,
	mb: 0.5,
};

export const ProductChangeDetailViewer = ({
	change,
	isSaving,
	isRecalculating,
	onSave,
	onClose,
	onRecalculate,
}: ProductChangeDetailViewerProps) => {
	const [formData, setFormData] = useState<ProductChange | null>(null);
	const [errors, setErrors] = useState<Record<string, string>>({});

	const isNewChange = !change?.Id;

	useEffect(() => {
		if (change) {
			setFormData(sanitizeNullStrings({ ...change }));
		} else {
			setFormData(null);
		}
		setErrors({});
	}, [change]);

	const handleChange = (field: keyof ProductChange, value: unknown) => {
		setFormData((prev) => (prev ? { ...prev, [field]: value } : null));
		setErrors((prev) => ({ ...prev, [field]: "" }));
	};

	const validate = (data: ProductChange | null): Record<string, string> => {
		const newErrors: Record<string, string> = {};
		if (!data?.InitialProductSizeId) {
			newErrors.InitialProductSizeId = "From product is required";
		}
		if (!data?.FinalProductSizeId) {
			newErrors.FinalProductSizeId = "To product is required";
		}
		if (
			data?.InitialProductSizeId &&
			data?.FinalProductSizeId &&
			data.InitialProductSizeId === data.FinalProductSizeId
		) {
			newErrors.FinalProductSizeId = "From and To cannot be the same product";
		}
		return newErrors;
	};

	const handleSelectChange = (field: keyof ProductChange, value: unknown) => {
		handleChange(field, value);

		if (isNewChange) return;

		const updatedData = formData ? { ...formData, [field]: value } : null;
		const validationErrors = validate(updatedData);
		setErrors(validationErrors);

		if (Object.keys(validationErrors).length > 0) return;

		if (change?.[field] !== value) {
			onSave({ ...formData!, [field]: value });
		}
	};

	const handleBlurSave = (field: keyof ProductChange, value: unknown) => {
		if (isNewChange) return;

		const updatedData = formData ? { ...formData, [field]: value } : null;
		const validationErrors = validate(updatedData);
		if (Object.keys(validationErrors).length > 0) return;

		if (change?.[field] !== value) {
			onSave({ ...formData!, [field]: value });
		}
	};

	const handleNumericBlurSave = (
		field: keyof ProductChange,
		rawValue: string,
	) => {
		const value = rawValue === "" ? null : Number.parseFloat(rawValue);
		handleChange(field, value);
		handleBlurSave(field, value);
	};

	const handleCreate = () => {
		const validationErrors = validate(formData);
		setErrors(validationErrors);

		if (formData && Object.keys(validationErrors).length === 0) {
			onSave(formData);
		}
	};

	if (!change) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100%",
					color: "text.secondary",
				}}
			>
				<Typography>Select a product change to view details</Typography>
			</Box>
		);
	}

	if (!formData) return null;

	return (
		<Box sx={{ p: 1.5, height: "100%", overflow: "auto" }}>
			{/* Header */}
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 1,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
						{isNewChange
							? "New Product Change"
							: `${formData.InitialProductSizeName ?? "ALL"} → ${formData.FinalProductSizeName ?? ""}`}
					</Typography>
					{(isSaving || isRecalculating) && <CircularProgress size={16} />}
				</Box>
				<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
					{isNewChange && (
						<Button
							color="primary"
							variant="contained"
							size="small"
							onClick={handleCreate}
							disabled={isSaving}
						>
							{isSaving ? "CREATING..." : "CREATE"}
						</Button>
					)}
					<Tooltip title="Close">
						<IconButton size="small" onClick={onClose} disabled={isSaving}>
							<CloseIcon />
						</IconButton>
					</Tooltip>
				</Box>
			</Box>

			<Divider sx={{ mb: 1 }} />

			<Grid2 container spacing={1}>
				{/* Product */}
				<Grid2 size={{ xs: 12 }}>
					<Typography sx={sectionHeaderSx}>Product</Typography>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<ProductSizeSelector
						value={formData.InitialProductSizeId}
						onChange={(value) =>
							handleSelectChange("InitialProductSizeId", value)
						}
						label="From"
						fullWidth
						size="small"
						required
						error={!!errors.InitialProductSizeId}
						helperText={errors.InitialProductSizeId}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<ProductSizeSelector
						value={formData.FinalProductSizeId}
						onChange={(value) =>
							handleSelectChange("FinalProductSizeId", value)
						}
						label="To"
						fullWidth
						size="small"
						required
						error={!!errors.FinalProductSizeId}
						helperText={errors.FinalProductSizeId}
					/>
				</Grid2>

				{/* Goal */}
				<Grid2 size={{ xs: 12 }}>
					<Typography sx={sectionHeaderSx}>Goal</Typography>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						label="Goal Time (min)"
						type="number"
						value={formData.GoalTime ?? ""}
						onChange={(e) =>
							handleChange(
								"GoalTime",
								e.target.value === ""
									? null
									: Number.parseFloat(e.target.value),
							)
						}
						onBlur={(e) => handleNumericBlurSave("GoalTime", e.target.value)}
						fullWidth
						size="small"
						slotProps={{
							inputLabel: { shrink: true },
							htmlInput: { step: 0.01 },
						}}
					/>
				</Grid2>

				{/* History (read-only) */}
				{!isNewChange && (
					<>
						<Grid2 size={{ xs: 12 }}>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
								}}
							>
								<Typography sx={sectionHeaderSx}>History</Typography>
								<Button
									variant="contained"
									color="primary"
									size="small"
									startIcon={<RefreshIcon />}
									onClick={() => formData.Id && onRecalculate(formData.Id)}
									disabled={isRecalculating || !formData.Id}
								>
									{isRecalculating ? "RECALCULATING..." : "RECALCULATE"}
								</Button>
							</Box>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<TextField
								label="History Quantity"
								value={formData.HistoryQuantity ?? ""}
								fullWidth
								size="small"
								disabled
								slotProps={{ inputLabel: { shrink: true } }}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<TextField
								label="History Time (min)"
								value={
									formData.HistoryTime != null
										? formData.HistoryTime.toFixed(2)
										: ""
								}
								fullWidth
								size="small"
								disabled
								slotProps={{ inputLabel: { shrink: true } }}
							/>
						</Grid2>
					</>
				)}
			</Grid2>
		</Box>
	);
};
